<template>
  <div class="course-list" v-loading="loading">
    <template v-if="isEdit">
      <el-button
        plain
        round
        size="small"
        type="primary"
        style="margin-bottom: 10px;"
        @click="chapterFormOpen('add', { courseCode: courseCode })"
        >新增章节</el-button
      >
      <el-card
        v-for="item in chapterList"
        :key="item.chapterId"
        style="margin-bottom: 16px;"
      >
        <div slot="header" class="clearfix">
          <span style="font-size: 16px; font-weight: bold;">
            章节名称： {{ item.chapterName }}</span
          >
          <div style="float: right;">
            <el-button
              plain
              round
              size="small"
              type="primary"
              @click="
                classFormOpen('add', {
                  chapterCode: item.chapterCode,
                  courseCode: item.courseCode
                })
              "
              >新增课时</el-button
            >
            <el-button
              plain
              round
              size="small"
              type="success"
              @click="chapterFormOpen('edit', item)"
              >编辑</el-button
            >
            <el-button
              plain
              round
              size="small"
              type="danger"
              @click="chapterDelete(item.chapterCode)"
              >删除</el-button
            >
          </div>
        </div>
        <el-table
          :data="classList[item.chapterCode]"
          style="width: 100%;"
          stripe
        >
          <el-table-column
            label="课时名称"
            prop="className"
            align="center"
          ></el-table-column>
          <el-table-column
            label="课时排序"
            prop="orderNum"
            align="center"
          ></el-table-column>
          <el-table-column label="课时时长" prop="classTime" align="center">
            <template #default="scope">
              {{ formatDuration(scope.row.classTime) }}
            </template>
          </el-table-column>
          <el-table-column label="防刷课" prop="antiBrushStatus" align="center">
            <template #default="scope">
              <el-switch
                v-model="scope.row.antiBrushStatus"
                :active-value="1"
                :inactive-value="0"
                @change="handleAntiBrushChange(scope.row)"
              />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="320" align="center">
            <template #default="scope">
              <el-button
                plain
                round
                size="small"
                type="primary"
                @click="videoPlayerOpen(scope.row)"
                >预览</el-button
              >
              <el-button
                plain
                round
                size="small"
                type="warning"
                @click="classQuestionOpen(scope.row)"
                >题目绑定</el-button
              >
              <el-button
                plain
                round
                size="small"
                type="success"
                @click="
                  classFormOpen('edit', {
                    classId: scope.row.classId,
                    chapterCode: item.chapterCode
                  })
                "
                >编辑</el-button
              >
              <el-button
                plain
                round
                size="small"
                type="danger"
                @click="classDelete(scope.row)"
                >删除</el-button
              >
            </template>
          </el-table-column>
        </el-table>
      </el-card>
      <div class="course-btn">
        <el-button plain round size="small" @click="previous">上一步</el-button>
        <el-button plain round size="small" type="primary" @click="next"
          >下一步</el-button
        >
        <el-button plain round size="small" @click="cancel">取消</el-button>
      </div>
    </template>

    <div v-else class="chapter-view-list">
      <div
        v-for="(item, index) in chapterList"
        :key="item.chapterId"
        class="chapter-view-item"
      >
        <div class="chapter-view-head">
          <div class="chapter-view-index" :style="getChapterIndexStyle(index)">
            {{ String(index + 1).padStart(2, "0") }}
          </div>
          <div class="chapter-view-content">
            <div class="chapter-view-name">{{ item.chapterName }}</div>
            <div class="chapter-view-desc">
              共 {{ (classList[item.chapterCode] || []).length }} 课时
            </div>
          </div>
          <div class="chapter-view-duration">
            <i class="el-icon-time"></i>
            总课时时长 {{ getChapterDuration(item.chapterCode) }}
          </div>
        </div>
        <div class="chapter-view-classes">
          <div
            v-for="(cls, cIndex) in classList[item.chapterCode] || []"
            :key="cls.classCode || cls.classId || cIndex"
            class="chapter-view-class"
          >
            <span class="chapter-view-class__index">{{ cIndex + 1 }}</span>
            <span class="chapter-view-class__name" :title="cls.className">
              {{ cls.className || "-" }}
            </span>
            <span class="chapter-view-class__time">
              <i class="el-icon-video-camera"></i>
              {{
                cls.classTime != null && cls.classTime !== ""
                  ? formatDuration(cls.classTime)
                  : "--:--"
              }}
            </span>
            <el-button
              plain
              round
              size="mini"
              type="primary"
              @click="videoPlayerOpen(cls)"
              >播放</el-button
            >
          </div>
          <div
            v-if="!(classList[item.chapterCode] || []).length"
            class="chapter-view-class-empty"
          >
            暂无课时
          </div>
        </div>
      </div>
      <div v-if="!chapterList.length" class="chapter-view-empty">暂无章节</div>
    </div>

    <ChapterForm @success="getChapterList" ref="chapterFormRef" />
    <ClassForm @success="getClassList" ref="classFormRef" />
    <VideoPlayerDialog ref="videoPlayerRef" />
    <QuestionForm ref="classQuestionRef" :editable="isEdit" />
  </div>
</template>
<script>
import {
  getChapterList,
  getChapterClassList,
  deleteChapter,
  deleteChapterClass,
  updateChapterClass
} from "@/api/course";
import { formatDuration } from "@/utils/util";
import ChapterForm from "./components/ChapterForm";
import ClassForm from "./components/ClassForm";
import VideoPlayerDialog from "@/components/VideoPlayerDialog";
import QuestionForm from "./components/QuestionForm";

/** 章节序号色系：按设计图自动生成 50 套（文字 / 背景 / 边框） */
function buildChapterIndexPalettes(count = 50) {
  const list = [];
  for (let i = 0; i < count; i++) {
    const hue = Math.round((360 / count) * i);
    list.push({
      color: `hsl(${hue}, 68%, 48%)`,
      background: `hsl(${hue}, 55%, 96%)`,
      borderColor: `hsl(${hue}, 45%, 88%)`
    });
  }
  return list;
}

const CHAPTER_INDEX_PALETTES = buildChapterIndexPalettes(50);

export default {
  name: "CourseList",
  components: {
    ChapterForm,
    ClassForm,
    VideoPlayerDialog,
    QuestionForm
  },
  props: {
    courseCode: String || Number,
    isEdit: { type: Boolean, default: true }
  },
  data() {
    return {
      loading: false,
      chapterList: [],
      activeNames: [],
      activeCache: [],
      classList: {},
      chapterIndexPalettes: CHAPTER_INDEX_PALETTES
    };
  },
  created() {
    this.getChapterList();
  },
  methods: {
    formatDuration,
    getChapterIndexStyle(index) {
      const palette = this.chapterIndexPalettes[
        index % this.chapterIndexPalettes.length
      ];
      return {
        color: palette.color,
        background: palette.background,
        borderColor: palette.borderColor
      };
    },
    getChapterDuration(chapterCode) {
      const list = this.classList[chapterCode] || [];
      const total = list.reduce(
        (sum, item) => sum + (Number(item.classTime) || 0),
        0
      );
      return total ? formatDuration(total) : "--:--";
    },
    chapterFormOpen(type, row = {}) {
      this.$refs.chapterFormRef.open(type, row, this.chapterList);
    },
    async getClassList(chapterCode) {
      const res = await getChapterClassList({
        chapterCode: chapterCode
      });
      const data = res && res.data;
      const list = Array.isArray(data)
        ? data
        : (data && (data.rows || data.list)) || [];
      this.$set(this.classList, chapterCode, list);
    },
    async getChapterList() {
      try {
        this.loading = true;
        const res = await getChapterList({ courseCode: this.courseCode });
        this.chapterList = res.data || [];
        for (let i = 0; i < this.chapterList.length; i++) {
          const chapterCode = this.chapterList[i].chapterCode;
          await this.getClassList(chapterCode);
        }
        this.$emit("loaded", this.chapterList.length);
      } finally {
        this.loading = false;
      }
    },
    classQuestionOpen(row) {
      this.$refs.classQuestionRef.open(row);
    },
    chapterDelete(chapterCode) {
      this.$confirm("确定删除该章节吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(async () => {
        await deleteChapter(chapterCode);
        await this.getChapterList();
        this.$message.success("章节删除成功");
      });
    },
    async handleAntiBrushChange(row) {
      try {
        await updateChapterClass(row);
        this.$message.success(
          `${row.antiBrushStatus === 1 ? "开启" : "关闭"}防刷课状态成功`
        );
      } catch (e) {
        this.$message.error(e.message);
        row.antiBrushStatus = row.antiBrushStatus === 1 ? 0 : 1;
      }
    },
    classDelete(row) {
      this.$confirm("确定删除该课时吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(async () => {
        await deleteChapterClass(row.classCode);
        await this.getClassList(row.chapterCode);
        this.$message.success("课时删除成功");
      });
    },
    cancel() {
      this.$router.push("/course");
    },
    classFormOpen(type, row = {}) {
      const list = row.chapterCode
        ? this.classList[row.chapterCode] || []
        : [];
      this.$refs.classFormRef.open(type, row, list);
    },
    videoPlayerOpen(row) {
      const url = row.urls || row.resourceUrl;
      if (!url) {
        this.$message.warning("暂无视频资源");
        return;
      }
      this.$refs.videoPlayerRef.open(url, row.className || "视频预览");
    },
    previous() {
      this.$emit("previous");
    },
    next() {
      this.$emit("next");
    }
  }
};
</script>
<style scoped lang="less">
.course-list {
  .class-item {
    margin: 0 10px 20px;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
    padding: 10px;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .course-btn {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }
  /deep/ .el-table__expanded-cell {
    padding: 0;
    border-bottom: none;
  }

  .chapter-view-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .chapter-view-item {
    padding: 16px 18px;
    background: rgba(99, 102, 241, 0.04);
    border-radius: 12px;
    transition: background 0.2s;

    &:hover {
      background: rgba(99, 102, 241, 0.08);
    }
  }

  .chapter-view-head {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .chapter-view-index {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    border: 1px solid;
    font-weight: 600;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-sizing: border-box;
  }

  .chapter-view-content {
    flex: 1;
    min-width: 0;
  }

  .chapter-view-name {
    font-size: 15px;
    font-weight: 600;
    color: #1d2129;
    margin-bottom: 4px;
  }

  .chapter-view-desc {
    font-size: 13px;
    color: #86909c;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .chapter-view-duration {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #86909c;
    font-size: 13px;
    flex-shrink: 0;
    min-width: 72px;
    white-space: nowrap;
  }

  .chapter-view-classes {
    margin-top: 12px;
    padding-left: 56px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .chapter-view-class {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: #fff;
    border-radius: 8px;
    border: 1px solid rgba(99, 102, 241, 0.08);

    &__index {
      flex-shrink: 0;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: rgba(99, 102, 241, 0.1);
      color: #6366f1;
      font-size: 12px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__name {
      flex: 1;
      min-width: 0;
      font-size: 13px;
      color: #1d2129;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__time {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: #86909c;
    }
  }

  .chapter-view-class-empty {
    padding: 8px 12px;
    font-size: 13px;
    color: #c0c4cc;
  }

  .chapter-view-empty {
    text-align: center;
    color: #86909c;
    padding: 24px 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/CourseEdit/CourseList.vue